import { DocIdParams } from '../dto/documents.js';
import { BacklinksV1, LinkCreateResultV1, LinkCreateV1, LinkParams, LinkReorderV1, LinksQueryV1, LinkTypeCreateRequestV1, LinkTypeV1, LinkUpdateV1, LinkV1, LinkWarningV1, TreeNodeV1, TreeQueryV1, } from '../dto/links.js';
import { listOf } from '../dto/list.js';
import { BaseRvQueryV1 } from '../dto/query.js';
import { defineRoute } from '../route/define-route.js';
import { HEADER_FROM_2L_S1 } from './change-batch-senders.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_BACKLINKS, EXAMPLE_LINK, EXAMPLE_LINK_CREATED, EXAMPLE_LINK_REORDERED, EXAMPLE_LINK_TYPE_CREATED, EXAMPLE_LINK_WARNING, EXAMPLE_TREE, } from './examples-links.js';
import { EXAMPLE_LINK_TYPES } from './examples-types.js';
/**
 * Typed links (1.0b deliverable 17), built by 1.C stage 1. A link belongs to its source document,
 * the `:id` of the path: an outgoing link is a write to that document (`deny`), and only a link's
 * target may be a reference document (`target-ok`). A row-version guard that fails answers 409
 * `row_conflict` with `details: {current: {rv}}`. A write whose source is trashed or merged answers
 * 409 `document_trashed` (CS r1c, DECISIONS 89 item 1); `EXAMPLE_LINK_REFUSALS` holds an example
 * of each answer that round added.
 */
const owned = {
    owner: '1.C',
    stage: '1',
    since: 'exit-1a',
    status: 'frozen',
    auth: 'session',
};
const ids = EXAMPLE_IDS;
/** Every link type, built-in and the GM's (a bounded list). */
export const linksTypes = defineRoute({
    id: 'links.types',
    method: 'GET',
    path: '/api/link-types',
    response: { kind: 'json', schema: listOf(LinkTypeV1) },
    reference: 'none',
    ...owned,
    examples: { response: { items: [...EXAMPLE_LINK_TYPES], next: null } },
});
export const linksCreateType = defineRoute({
    id: 'links.createType',
    method: 'POST',
    path: '/api/link-types',
    body: LinkTypeCreateRequestV1,
    response: { kind: 'json', schema: LinkTypeV1, status: 201 },
    reference: 'none',
    ...owned,
    examples: {
        request: { body: { label: 'Sworn to', inverseLabel: 'Sworn by' } },
        response: EXAMPLE_LINK_TYPE_CREATED,
    },
});
/** One document's links (a bounded list): out of it, into it, or both. */
export const linksList = defineRoute({
    id: 'links.list',
    method: 'GET',
    path: '/api/docs/:id/links',
    params: DocIdParams,
    query: LinksQueryV1,
    response: { kind: 'json', schema: listOf(LinkV1) },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    ...owned,
    examples: {
        request: { params: { id: ids.questSaltInTheWells.id }, query: { direction: 'out' } },
        response: { items: [EXAMPLE_LINK], next: null },
    },
});
/**
 * Link the document to a target. `isPrimary` on a type that is not structural answers 422
 * `primary_not_structural`; a primary link that would make a cycle 422 `link_cycle`; a trashed or
 * merged source or target 409 `document_trashed`; a validity boundary unknown, trashed or refused
 * 422 `invalid_body` with `details.path` naming it (CS r3; `EXAMPLE_LINK_DATED` sends one).
 */
export const linksCreate = defineRoute({
    id: 'links.create',
    method: 'POST',
    path: '/api/docs/:id/links',
    params: DocIdParams,
    body: LinkCreateV1,
    response: { kind: 'json', schema: LinkCreateResultV1, status: 201 },
    errors: {
        409: ['document_trashed'],
        422: ['link_cycle', 'primary_not_structural', 'invalid_body'],
    },
    reference: 'target-ok',
    docIds: { owner: ['params.id'], target: ['body.targetId'] },
    ...owned,
    examples: {
        request: {
            params: { id: ids.questMissingLamplighters.id },
            body: { linkTypeKey: 'related_to', targetId: ids.npcMaelisDorn.id },
        },
        response: EXAMPLE_LINK_CREATED,
    },
});
const writeConflicts = { 409: ['row_conflict', 'document_trashed'] };
/**
 * Change a link's own text and meta, or (CS r1c, DECISIONS 89 item 2) make its source the home of
 * its target with `isPrimary: true` (`false` clears the home and keeps the link): the checks of
 * `links.create` apply, 422 `primary_not_structural` and `link_cycle`. A validity boundary unknown,
 * trashed or refused answers 422 `invalid_body` (CS r3).
 */
export const linksUpdate = defineRoute({
    id: 'links.update',
    method: 'PATCH',
    path: '/api/docs/:id/links/:linkId',
    params: LinkParams,
    body: LinkUpdateV1,
    response: { kind: 'json', schema: LinkV1 },
    errors: { ...writeConflicts, 422: ['link_cycle', 'primary_not_structural', 'invalid_body'] },
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...owned,
    examples: {
        request: {
            params: { id: ids.questSaltInTheWells.id, linkId: ids.linkSaltGivenByOrla.id },
            body: { baseRv: 1, note: 'She pays in salt, not coin.' },
        },
        response: { ...EXAMPLE_LINK, rv: 2, note: 'She pays in salt, not coin.' },
        consumes: true,
    },
});
export const linksRemove = defineRoute({
    id: 'links.remove',
    method: 'DELETE',
    path: '/api/docs/:id/links/:linkId',
    params: LinkParams,
    query: BaseRvQueryV1,
    response: { kind: 'empty' },
    errors: writeConflicts,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...owned,
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2L_S1,
    examples: {
        request: {
            params: { id: ids.itemTidebell.id, linkId: ids.linkTidebellRelatedToLanternDuplicate.id },
            query: { baseRv: 1 },
        },
        response: null,
        consumes: true,
    },
});
/** Move one of the document's links after another (null: first); the Story panel's order. */
export const linksReorder = defineRoute({
    id: 'links.reorder',
    method: 'POST',
    path: '/api/docs/:id/links/reorder',
    params: DocIdParams,
    body: LinkReorderV1,
    response: { kind: 'json', schema: LinkV1 },
    errors: writeConflicts,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...owned,
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2L_S1,
    examples: {
        request: {
            params: { id: ids.arcRisingWater.id },
            body: { linkId: ids.linkArcContainsSaltInTheWells.id, afterLinkId: null, baseRv: 1 },
        },
        response: EXAMPLE_LINK_REORDERED,
        consumes: true,
    },
});
/** Duplicate and overlapping links of the document, each with its offers. */
export const linksLint = defineRoute({
    id: 'links.lint',
    method: 'GET',
    path: '/api/docs/:id/links/lint',
    params: DocIdParams,
    response: { kind: 'json', schema: listOf(LinkWarningV1) },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    ...owned,
    examples: {
        request: { params: { id: ids.itemTidebell.id } },
        response: { items: [EXAMPLE_LINK_WARNING], next: null },
    },
});
export const linksBacklinks = defineRoute({
    id: 'links.backlinks',
    method: 'GET',
    path: '/api/docs/:id/backlinks',
    params: DocIdParams,
    response: { kind: 'json', schema: BacklinksV1 },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    ...owned,
    examples: { request: { params: { id: ids.npcOrlaVenn.id } }, response: EXAMPLE_BACKLINKS },
});
/** The story tree: the documents homed under one parent (the top level when omitted). */
export const linksTree = defineRoute({
    id: 'links.tree',
    method: 'GET',
    path: '/api/tree',
    query: TreeQueryV1,
    response: { kind: 'json', schema: listOf(TreeNodeV1) },
    reference: 'read',
    docIds: { owner: ['query.parentId'] },
    ...owned,
    examples: {
        request: { query: { parentId: ids.arcRisingWater.id } },
        response: { items: EXAMPLE_TREE, next: null },
    },
});
//# sourceMappingURL=links.js.map